import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme, useMediaQuery } from '@mui/material';
import { useHelp } from '../../contexts/HelpContext';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import { ConfigContext } from '../shared/ConfigContext';
import { fetchPublicPlans } from '../../api/planClient';
import { usePlanLimitContent, PLAN_LIMIT_PREFIX, fmtDate } from './planLimitContent';
import { useHelpBundle } from './helpBundle';
import eventBus from '../../utils/eventBus';

// Page + useXPage hook split, same as every other feature in this codebase
// (see useInvoicesPage.js) — HelpPanel.js stays a pure view, all state and
// content lookups live here.
const useHelpPanel = () => {
  const { t } = useTranslation('help');
  const bundle = useHelpBundle();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const help = useHelp();
  const config = useContext(ConfigContext);
  const { usage, subscription, periodEnd } = useEntitlements();

  // Plan catalog, fetched lazily only once the panel is actually opened and
  // only for tenant roles — global admins have no company-scoped plan.
  const [plans, setPlans] = useState(null);
  useEffect(() => {
    if (!help.isOpen || plans !== null || !config?.backendUrl) return;
    if (localStorage.getItem('userRole') === 'global_admin') { setPlans([]); return; }
    fetchPublicPlans(config.backendUrl)
      .then((res) => setPlans(res.data?.plans || res.data || []))
      .catch(() => setPlans([]));
  }, [help.isOpen, plans, config]);

  const pageTopics = useMemo(
    () => bundle.pages?.[help.route]?.topics || [],
    [bundle, help.route],
  );
  const pageLabel = bundle.pages?.[help.route]?.label || '';
  const popular = useMemo(() => bundle.popular || [], [bundle]);
  // Only routes with an authored tour show the launcher card (HelpTour ships
  // in step 4 — this makes the card appear automatically as tours are added
  // without any further change here).
  const tourMeta = bundle.tours?.[help.route] || null;

  // A topic flagged `live: true` (the plan-limit topic) shows its current
  // usage right in the topic list, not just once the article is opened —
  // matches the mockup's "17 of 20 used · resets Oct 3" subtitle.
  const topicsWithLiveSubtitle = useMemo(() => pageTopics.map((topic) => {
    if (!topic.live || !topic.articleId || !topic.articleId.startsWith(PLAN_LIMIT_PREFIX)) return topic;
    const featureKey = topic.articleId.slice(PLAN_LIMIT_PREFIX.length);
    const u = usage(featureKey);
    if (!u || u.used == null) return topic;
    const subtitle = u.limit == null
      ? t('planLimit.topicLiveSubtitleUnlimited', { used: u.used })
      : t('planLimit.topicLiveSubtitle', { used: u.used, limit: u.limit, renewsOn: fmtDate(subscription?.currentPeriodEnd || periodEnd) });
    return { ...topic, subtitle };
  }), [pageTopics, usage, subscription, periodEnd, t]);

  const getStaticArticle = useCallback((articleId) => {
    if (!articleId || articleId.startsWith(PLAN_LIMIT_PREFIX)) return null;
    return bundle.articles?.[articleId] || null;
  }, [bundle]);

  const activeFeatureKey = help.activeArticleId && help.activeArticleId.startsWith(PLAN_LIMIT_PREFIX)
    ? help.activeArticleId.slice(PLAN_LIMIT_PREFIX.length)
    : null;
  const planLimitContent = usePlanLimitContent(activeFeatureKey, plans);
  const activeArticle = activeFeatureKey ? null : getStaticArticle(help.activeArticleId);

  // Client-side search index over every page's topics — built once per
  // render of the (static) JSON bundle. Cheap: ~75 articles is a few KB.
  const searchIndex = useMemo(() => {
    const pages = bundle.pages || {};
    const articles = bundle.articles || {};
    const items = [];
    Object.entries(pages).forEach(([routeKey, page]) => {
      (page.topics || []).forEach((topic) => {
        const article = articles[topic.articleId];
        items.push({
          route: routeKey,
          pageLabel: page.label,
          topicId: topic.id,
          articleId: topic.articleId,
          title: topic.title,
          snippet: topic.subtitle || (article && article.body ? article.body.slice(0, 120) : ''),
          keywords: `${topic.title} ${topic.subtitle || ''} ${(article && article.body) || ''}`.toLowerCase(),
        });
      });
    });
    return items;
  }, [bundle]);

  const search = useCallback((query) => {
    const q = (query || '').trim().toLowerCase();
    const matches = q ? searchIndex.filter((item) => item.keywords.includes(q)) : [...searchIndex];
    return matches.sort((a, b) => {
      const aHere = a.route === help.route ? 0 : 1;
      const bHere = b.route === help.route ? 0 : 1;
      return aHere - bHere;
    });
  }, [searchIndex, help.route]);

  const results = useMemo(
    () => (help.view === 'search' ? search(help.searchQuery) : []),
    [help.view, help.searchQuery, search],
  );
  const resultsOnThisPage = useMemo(
    () => results.filter((r) => r.route === help.route).length,
    [results, help.route],
  );

  useEffect(() => {
    if (help.view === 'search' && help.searchQuery.trim() && results.length === 0) {
      eventBus.emit(eventBus.EVENTS.HELP_SEARCH_ZERO_RESULTS, { query: help.searchQuery, route: help.route });
    }
    // Only re-fire when the query itself changes, not on every result recompute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [help.view, help.searchQuery]);

  const openTopic = useCallback((topic) => {
    eventBus.emit(eventBus.EVENTS.HELP_TOPIC_OPEN, { route: help.route, topicId: topic.id, articleId: topic.articleId });
    help.openArticle(topic.articleId, 'topics');
  }, [help]);

  const openResult = useCallback((result) => {
    eventBus.emit(eventBus.EVENTS.HELP_TOPIC_OPEN, { route: result.route, topicId: result.topicId, articleId: result.articleId, viaSearch: true });
    help.openArticle(result.articleId, 'search');
  }, [help]);

  const lastArticleForRoute = help.lastArticleForRoute;
  const lastArticlePreview = useMemo(() => {
    if (!lastArticleForRoute) return null;
    // Prefer this route's own topic title (covers the plan-limit topic too,
    // whose title is authored copy, not the generic template string).
    const topicMatch = topicsWithLiveSubtitle.find((topic) => topic.articleId === lastArticleForRoute);
    if (topicMatch) return { articleId: lastArticleForRoute, title: topicMatch.title };
    const article = getStaticArticle(lastArticleForRoute);
    return article ? { articleId: lastArticleForRoute, title: article.title } : null;
  }, [lastArticleForRoute, topicsWithLiveSubtitle, getStaticArticle]);

  return {
    t, isMobile, help,
    pageTopics: topicsWithLiveSubtitle, pageLabel, popular, tourMeta,
    activeArticle, activeFeatureKey, planLimitContent,
    results, resultsOnThisPage, search,
    openTopic, openResult,
    lastArticlePreview,
  };
};

export default useHelpPanel;
